import {
  AlignBottomIcon,
  AlignCenterVerticalIcon,
  AlignTopIcon,
  TextAlignCenterIcon,
  TextAlignLeftIcon,
  TextAlignRightIcon,
} from '@persona-id/design-tokens/icons';
import { noop } from 'lodash/fp';
import { useCallback, useMemo } from 'react';

import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import {
  ColorStyleInput,
  FontFamilySelect,
  FontWeightSelect,
  StyleInput,
  ToggleStyleInput,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs';
import {
  SectionColumn,
  SectionRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/shared';
import {
  StyleValueAlignment,
  StyleValueColorConfig,
  StyleValueFontFamily,
  StyleValueFontWeight,
  StyleValueJustify,
  StyleValueSizeConfig,
} from 'lib/inquiryStyles';
import {
  TextKeys,
  TextLayoutKeys,
  TextStylingComponent,
  TextStylingComponentConfig,
} from 'lib/interfaces/ui-components';

type TextStyleAttributes = {
  'font-family': {
    label: string;
    default: StyleValueFontFamily;
  };
  'font-weight': {
    label: string;
    default: StyleValueFontWeight;
  };
  'font-size': {
    label: string;
    default: StyleValueSizeConfig;
  };
  'line-height': {
    label: string;
    default: StyleValueSizeConfig;
  };
  'letter-spacing': {
    label: string;
    default: StyleValueSizeConfig;
  };
  'text-color': {
    label: string;
    default: StyleValueColorConfig;
  };
  justify: {
    label: string;
    default: StyleValueJustify;
  };
  alignment: {
    label: string;
    default: StyleValueAlignment;
  };
  'text-color-highlight': {
    label: string;
    default: StyleValueColorConfig;
  };
};

const DEFAULT_TEXT_ATTRIBUTES: TextStyleAttributes = {
  'font-family': {
    label: 'Font family',
    default: '',
  },
  'font-weight': {
    label: 'Font weight',
    default: StyleValueFontWeight.Normal,
  },
  'font-size': {
    label: 'Font size',
    default: {
      value: 0,
      unit: 'px',
    },
  },
  'line-height': {
    label: 'Line height',
    default: {
      value: 0,
      unit: 'px',
    },
  },
  'letter-spacing': {
    label: 'Letter spacing',
    default: {
      value: 0,
      unit: 'px',
    },
  },
  'text-color': {
    label: 'Text color',
    default: {
      value: '',
      unit: 'hex',
    },
  },
  justify: {
    label: 'Alignment',
    default: StyleValueJustify.Start,
  },
  alignment: {
    label: 'Alignment',
    default: StyleValueAlignment.Start,
  },
  'text-color-highlight': {
    label: 'Link color',
    default: {
      value: '',
      unit: 'hex',
    },
  },
};

const ALIGNMENT_OPTIONS: Record<
  TextLayoutKeys,
  { label: JSX.Element; value: StyleValueAlignment | StyleValueJustify }[]
> = {
  justify: [
    { label: <TextAlignLeftIcon size={12} />, value: StyleValueJustify.Start },
    { label: <TextAlignCenterIcon size={12} />, value: StyleValueJustify.Center },
    { label: <TextAlignRightIcon size={12} />, value: StyleValueJustify.End },
  ],
  alignment: [
    { label: <AlignBottomIcon size={12} />, value: StyleValueAlignment.Start },
    { label: <AlignCenterVerticalIcon size={12} />, value: StyleValueAlignment.Center },
    { label: <AlignTopIcon size={12} />, value: StyleValueAlignment.End },
  ],
};

type TextStylesProps = {
  componentPreview: TextStylingComponent;
  component: TextStylingComponentConfig;
  baseComponentConfig: TextStylingComponentConfig;
  componentBasePath: string;
  styleVariantIndex: number;
  editable: boolean;
  submitStyle: (field: string, values: any) => void;
  path: string;
  disabledFields?: TextKeys[];
};

const TextStyles = ({
  componentPreview,
  component,
  baseComponentConfig,
  styleVariantIndex,
  editable,
  submitStyle,
  path,
  disabledFields = [],
}: TextStylesProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const renderable = Object.keys(baseComponentConfig['style-variants'][0]).filter(
    (key) => key in DEFAULT_TEXT_ATTRIBUTES,
  );

  const textDefaults: TextStyleAttributes = useMemo(() => {
    const textMapTuples = renderable.map((renderableStyle: TextKeys) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const styleDefaults = DEFAULT_TEXT_ATTRIBUTES[renderableStyle];
      const defaultValue =
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        componentPreview.styles?.[renderableStyle]?.base?.base || styleDefaults.default;
      return [
        renderableStyle,
        {
          ...styleDefaults,
          default: defaultValue,
        },
      ];
    });
    return Object.fromEntries(textMapTuples);
  }, [componentPreview.styles, renderable]);

  const submitInputField = useCallback(
    (value: string, field: string) => {
      const savedValue = parseInt(value) >= 0 ? parseInt(value) : 0;
      submitStyle(field, {
        value: savedValue,
        unit: 'px',
      });
    },
    [submitStyle],
  );

  const onToggleChange = useCallback(
    (value: StyleValueJustify, field: string) => {
      submitStyle(field, value);
    },
    [submitStyle],
  );

  const onColorInputChange = useCallback(
    (hex: string, field: string) => {
      submitStyle(field, { value: hex, unit: 'hex' });
    },
    [submitStyle],
  );

  // For pure text components, justify is included in the style variant
  // For form input components, alignment is either included, or neither is
  // in which case we disable the setting
  const textAlignmentToggleSetting = useMemo(() => {
    const hasJustifySetting = textDefaults['justify'] != null;
    const alignmentSetting = hasJustifySetting
      ? textDefaults['justify']
      : textDefaults['alignment'] || null;

    if (alignmentSetting == null)
      return (
        <ToggleStyleInput
          label={DEFAULT_TEXT_ATTRIBUTES['alignment'].label}
          field=''
          editable={false}
          name=''
          defaultValue=''
          options={[]}
          onChange={noop}
        />
      );

    const field = hasJustifySetting ? 'justify' : 'alignment';

    return (
      <ToggleStyleInput
        label={alignmentSetting.label}
        field={field}
        editable={editable && !disabledFields.includes(field)}
        name={`text-${field}-toggle`}
        defaultValue={alignmentSetting.default}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        options={ALIGNMENT_OPTIONS[field]}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onChange={onToggleChange}
      />
    );
  }, [textDefaults, disabledFields, onToggleChange, editable]);

  return (
    <SectionAccordion title='Text'>
      <SectionRow>
        <SectionColumn>
          <FontFamilySelect
            editable={editable}
            defaultValue={textDefaults['font-family'].default}
            path={path}
          />
        </SectionColumn>
        <SectionColumn>
          <FontWeightSelect
            editable={editable}
            defaultValue={textDefaults['font-weight'].default}
            path={path}
          />
        </SectionColumn>
      </SectionRow>
      <SectionRow>
        <SectionColumn>
          <StyleInput
            field='font-size'
            name={`component-style-font-size-${component.name}-${styleVariantIndex}`}
            label={textDefaults['font-size'].label}
            defaultValue={textDefaults['font-size'].default.value.toString()}
            editable={editable}
            submit={submitInputField}
          />
        </SectionColumn>
        <SectionColumn>
          <StyleInput
            field='line-height'
            name={`component-style-line-height-${component.name}-${styleVariantIndex}`}
            label={textDefaults['line-height'].label}
            defaultValue={textDefaults['line-height'].default.value.toString()}
            editable={editable}
            submit={submitInputField}
          />
        </SectionColumn>
      </SectionRow>
      <SectionRow>
        <SectionColumn>
          <StyleInput
            field='letter-spacing'
            name={`component-style-letter-spacing-${component.name}-${styleVariantIndex}`}
            label={textDefaults['letter-spacing'].label}
            defaultValue={textDefaults['letter-spacing'].default.value.toString()}
            editable={editable}
            submit={submitInputField}
          />
        </SectionColumn>
        <SectionColumn>{textAlignmentToggleSetting}</SectionColumn>
      </SectionRow>
      <SectionRow>
        <SectionColumn>
          <ColorStyleInput
            label={textDefaults['text-color'].label}
            field='text-color'
            name={component.name}
            onChange={onColorInputChange}
            value={textDefaults['text-color'].default.value}
            editable={editable}
          />
        </SectionColumn>
      </SectionRow>
      {textDefaults['text-color-highlight'] != null && (
        <SectionRow>
          <SectionColumn>
            <ColorStyleInput
              label={textDefaults['text-color-highlight'].label}
              field='text-color-highlight'
              name={component.name}
              onChange={onColorInputChange}
              value={textDefaults['text-color-highlight'].default.value}
              editable={editable}
            />
          </SectionColumn>
        </SectionRow>
      )}
    </SectionAccordion>
  );
};

export default TextStyles;
